import { auth, db } from '../config/firebase';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

// Type definitions for cross-platform compatibility
interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  createdAt: Date;
  lastLoginAt: Date;
  loyaltyPoints: number;
  preferences: {
    notifications: boolean;
    language: string;
  };
}

class AuthService {
  private currentUser: User | null = null;

  // Listen to authentication state changes
  onAuthStateChanged(callback: (user: User | null) => void) {
    const { onAuthStateChanged } = require('firebase/auth');
    return onAuthStateChanged(auth, callback);
  }

  // Register new user
  async register(email: string, password: string, displayName: string): Promise<User> {
    try {
      const { createUserWithEmailAndPassword, updateProfile } = require('firebase/auth');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile
      await updateProfile(userCredential.user, {
        displayName: displayName
      });

      const user = userCredential.user;
      
      // Create user profile in Firestore
      await this.createUserProfile(user, { displayName });
      
      // Store auth token securely
      const token = await this.getIdToken(user);
      await SecureStore.setItemAsync('authToken', token);
      
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Sign in user
  async signIn(email: string, password: string): Promise<User> {
    try {
      const { signInWithEmailAndPassword } = require('firebase/auth');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      const user = userCredential.user;
      
      // Store auth token securely
      const token = await this.getIdToken(user);
      await SecureStore.setItemAsync('authToken', token);
      
      // Update last login time
      await this.updateUserProfile(user.uid, { lastLoginAt: new Date() });
      
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  // Sign out user
  async signOut(): Promise<void> {
    try {
      const { signOut } = require('firebase/auth');
      await signOut(auth);
      
      // Remove stored token
      await SecureStore.deleteItemAsync('authToken');
      this.currentUser = null;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    try {
      const { sendPasswordResetEmail } = require('firebase/auth');
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUser || auth.currentUser;
  }

  // Get ID token
  private async getIdToken(user: any): Promise<string> {
    if (Platform.OS === 'web') {
      return await user.getIdToken();
    } else {
      return await user.getIdToken();
    }
  }

  // Get stored auth token
  async getStoredToken(): Promise<string | null> {
    try {
      return await SecureStore.getItemAsync('authToken');
    } catch (error) {
      console.error('Error getting stored token:', error);
      return null;
    }
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getStoredToken();
    return !!token && !!this.getCurrentUser();
  }

  // Create user profile in Firestore
  private async createUserProfile(user: any, additionalData: any = {}): Promise<void> {
    try {
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email,
        displayName: additionalData.displayName || user.displayName || '',
        photoURL: user.photoURL,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        loyaltyPoints: 0,
        preferences: {
          notifications: true,
          language: 'tr'
        },
        ...additionalData
      };

      const { doc, setDoc } = require('firebase/firestore');
      await setDoc(doc(db, 'users', user.uid), userProfile);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(uid: string, data: Partial<UserProfile>): Promise<void> {
    try {
      const { doc, setDoc } = require('firebase/firestore');
      await setDoc(doc(db, 'users', uid), data, { merge: true });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Get user profile
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const { doc, getDoc } = require('firebase/firestore');
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as UserProfile;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }
}

export default new AuthService();